import clsx from 'clsx';
import type { ReactNode } from 'react';

/**
 * What the People & work pages' charts share. The series colours are the tokens' own steps, chosen so each
 * one reads on the card in its theme and stays apart for colour-blind readers (checked light on #FFFFFF and
 * dark on #1A1B1F): crimson leads, then info cyan, then a deep amber; crimson lifts a step in the dark theme.
 * They are CSS variables set on the chart's frame, so switching the theme repaints the marks by itself.
 *
 * Every chart is drawn left to right (time and categories read that way in both languages) and has a table
 * twin: the figures are never reachable through the tooltip alone.
 */

export const SERIES = ['var(--b-chart-1)', 'var(--b-chart-2)', 'var(--b-chart-3)'] as const;

const FRAME_VARS =
  '[--b-chart-1:theme(colors.primary.500)] dark:[--b-chart-1:theme(colors.primary.400)] [--b-chart-2:theme(colors.info.DEFAULT)] [--b-chart-3:theme(colors.signal.700)]';

/** Hairline, solid, one step off the card: the grid never competes with the data. */
export const GRID = 'var(--line)';
export const AXIS = { stroke: 'var(--text-tertiary)', fontSize: 12 } as const;

/** Text in a tooltip wears the text tokens, never the series colour. */
export const tooltipStyle = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  color: 'var(--text-primary)',
  fontSize: 13,
} as const;
export const tooltipItemStyle = { color: 'var(--text-primary)' } as const;
export const tooltipLabelStyle = { color: 'var(--text-secondary)' } as const;

/** A dot with a ring in the card's colour, so it stays legible where it meets the line. */
export const dotStyle = (fill: string) => ({ r: 4, strokeWidth: 2, stroke: 'var(--surface-card)', fill });

/** The chart's box: its colours, its reading direction and a summary for screen readers. */
export function ChartFrame({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <figure className={clsx(FRAME_VARS, 'm-0 h-64', className)} dir="ltr" role="img" aria-label={label}>
      {children}
    </figure>
  );
}

/** The chart's figures as a table, one click away. */
export function TableTwin({ summary, children }: { summary: string; children: ReactNode }) {
  return (
    <details className="mt-3">
      <summary className="cursor-pointer select-none text-caption text-[var(--text-secondary)] hover:text-[var(--text-primary)]">{summary}</summary>
      <div className="mt-2">{children}</div>
    </details>
  );
}

/** A legend entry's words, in the text tokens. */
export function legendText(value: string) {
  return <span className="text-caption text-[var(--text-secondary)]">{value}</span>;
}
